/** Produse (App-Produse.dc.html) — CRUD + import URL, „Clip cu produsul ăsta" (ușa 3). */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProducts } from '../api/endpoints';
import { createProduct, deleteProduct, importProductUrl } from '../api/endpoints';
import { useMutation, useInvalidate, qk } from '../api/hooks';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { useRealtime } from '../context/RealtimeContext';
import { EmptyState, Modal, PageHeader, Skeleton, Spinner } from '../components/ui';
import type { Product } from '../api/types';

/** Statusul importului dintr-un produs (atributul persistat de backend). */
function importStatus(product: Product): string | undefined {
  const imp = product.attributes?.import as { status?: string } | undefined;
  return imp?.status;
}

export function ProdusePage() {
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  const toast = useToast();
  const { connected, onEvent } = useRealtime();
  const [pollUntil, setPollUntil] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    document.title = 'Produse — Reclame Video';
  }, []);

  // Progresul importului vine pe WebSocket (`import.progress`, emis din ProductsService).
  // Fallback: dacă socket-ul nu e conectat, revenim la polling (fereastră scurtă după start).
  const pollingFallback = !connected && Date.now() < pollUntil;
  const products = useQuery({
    queryKey: qk.products,
    queryFn: () => getProducts(),
    refetchInterval: pollingFallback ? 2000 : false,
  });

  // Ascultă `import.progress` prin RealtimeContext: la fiecare progres reîmprospătează
  // lista; la final (`done`/`failed`) afișează un toast. Înlocuiește polling-ul cât timp
  // socket-ul e conectat.
  useEffect(() => {
    return onEvent((event, payload) => {
      if (event !== 'import.progress') return;
      const p = (payload ?? {}) as {
        productId?: string;
        pct?: number;
        status?: string;
        error?: string;
        mode?: string;
        queued?: number;
        found?: number;
        fromExplore?: boolean;
      };
      qc.invalidateQueries({ queryKey: qk.products });
      if (p.mode === 'explore') {
        if (p.status === 'failed') {
          toast.push({ kind: 'err', title: 'Explorare eșuată', body: p.error || 'Nu am putut scana pagina.' });
        } else if (p.status === 'queued' && p.pct === 100 && typeof p.queued === 'number') {
          toast.push({
            kind: 'ok',
            title: 'Produse găsite',
            body: `Am pus la import ${p.queued} produs${p.queued === 1 ? '' : 'e'}.`,
          });
        }
        return;
      }
      // Importuri din explore: fără toast per produs (evităm spam la max 20).
      if (p.fromExplore) return;
      if (p.status === 'done') {
        toast.push({ kind: 'ok', title: 'Import gata', body: 'Am completat automat numele, prețul și pozele.' });
      } else if (p.status === 'failed') {
        toast.push({ kind: 'err', title: 'Import eșuat', body: p.error || 'Nu am putut prelua pagina.' });
      }
    });
  }, [onEvent, qc, toast]);

  const importMut = useMutation({
    mutationFn: ({ url, explore }: { url: string; explore: boolean }) => importProductUrl(url, { explore }),
    onSuccess: (_data, vars) => {
      invalidate([qk.products]);
      setPollUntil(Date.now() + (vars.explore ? 90000 : 30000));
      toast.push({
        kind: 'ok',
        title: vars.explore ? 'Explorare pornită' : 'Import pornit',
        body: vars.explore
          ? 'Scanăm pagina și importăm până la 20 de produse.'
          : 'Luăm automat numele, prețul și pozele de pe pagină.',
      });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Import eșuat', body: humanError(err) }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      invalidate([qk.products]);
      toast.push({ kind: 'info', title: 'Produs șters' });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });

  const items = products.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Produse"
        subtitle="Date structurate persistente — numele, prețul, beneficiile și pozele intră automat în contextul AI."
        actions={<button className="btn btn-primary btn-sm" data-testid="add-product" onClick={() => setAddOpen(true)}>+ Produs nou</button>}
      />

      <ImportBar onImport={(url, explore) => importMut.mutate({ url, explore })} busy={importMut.isPending} />

      {products.isLoading ? (
        <div className="grid-auto" style={{ marginTop: 18 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={150} />)}</div>
      ) : items.length === 0 ? (
        <div style={{ marginTop: 18 }}>
          <EmptyState illustration="📦" title="Niciun produs încă" body="Lipește un link de produs sau adaugă manual — AI-ul îi va folosi datele în clipuri." />
        </div>
      ) : (
        <div className="grid-auto" style={{ marginTop: 18 }} data-testid="products-grid">
          {items.map((p) => (
            <ProductCard key={p.id} product={p} onDelete={() => deleteMut.mutate(p.id)} onUse={() => navigate(`/wizard?door=produs`)} />
          ))}
        </div>
      )}

      <AddProductModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => { invalidate([qk.products]); setAddOpen(false); }} />
    </div>
  );
}

function ImportBar({ onImport, busy }: { onImport: (url: string, explore: boolean) => void; busy: boolean }) {
  const [url, setUrl] = useState('');
  const [explore, setExplore] = useState(false);
  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 800 }}>Lipește URL-ul:</span>
        <input
          className="input"
          data-testid="import-url"
          placeholder={explore ? 'https://magazinul-tau.ro sau /categorie' : 'https://magazinul-tau.ro/produs'}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <button
          className="btn btn-primary btn-sm"
          data-testid="import-btn"
          onClick={() => {
            onImport(url, explore);
            setUrl('');
          }}
          disabled={busy || !url.trim()}
        >
          {busy ? <Spinner /> : explore ? 'Explorează & importă' : 'Importă automat'}
        </button>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
        <input
          type="checkbox"
          data-testid="import-explore"
          checked={explore}
          onChange={(e) => setExplore(e.target.checked)}
        />
        <span>
          <strong style={{ color: 'var(--text)' }}>Explorează site-ul</strong>
          {' '}(max 20 produse) — bifat = scanează linkuri de pe pagină; debifat = pagina e produsul
        </span>
      </label>
    </div>
  );
}

function ProductCard({ product, onDelete, onUse }: { product: Product; onDelete: () => void; onUse: () => void }) {
  const status = importStatus(product);
  const importing = status === 'pending';
  const failed = status === 'failed';
  return (
    <div className="card" data-testid="product-card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 800, fontSize: 15, flex: 1 }} data-testid="product-name">{product.name}</span>
        {importing && <span className="pill" data-testid="product-importing" style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>importăm…</span>}
        {failed && <span className="pill" style={{ background: 'var(--err-soft)', color: 'var(--err)' }}>import eșuat</span>}
      </div>
      {product.priceText && <span style={{ fontSize: 13, color: 'var(--muted)' }}>{product.priceText}</span>}
      {product.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {product.tags.slice(0, 4).map((t) => (
            <span key={t} className="pill" style={{ background: 'var(--bg2)', color: 'var(--muted)' }}>{t}</span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <button className="btn btn-ghost btn-sm" onClick={onUse} style={{ flex: 1 }}>Clip cu produsul ăsta</button>
        <button className="btn btn-ghost btn-sm" onClick={onDelete} style={{ color: 'var(--err)' }} aria-label="Șterge">✕</button>
      </div>
    </div>
  );
}

function AddProductModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: '', priceText: '', description: '' });
  const toast = useToast();
  const mut = useMutation({
    mutationFn: () => createProduct({ name: form.name, priceText: form.priceText || undefined, description: form.description || undefined }),
    onSuccess: () => {
      toast.push({ kind: 'ok', title: 'Produs adăugat' });
      setForm({ name: '', priceText: '', description: '' });
      onCreated();
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });
  return (
    <Modal open={open} onClose={onClose} title="Produs nou">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label className="label">Nume<input className="input" data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="label">Preț (text)<input className="input" placeholder="ex. 49 lei" value={form.priceText} onChange={(e) => setForm({ ...form, priceText: e.target.value })} /></label>
        <label className="label">Descriere<textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
        <button className="btn btn-primary" data-testid="save-product" onClick={() => mut.mutate()} disabled={mut.isPending || form.name.trim().length < 2}>
          {mut.isPending ? <Spinner /> : 'Adaugă produsul'}
        </button>
      </div>
    </Modal>
  );
}
